// Picker de poses para el modo "Imagen con modelo" de Interfaz V2.
//
// Multi-select de poses del catálogo (`poses` table). Dos variantes:
//
//   • ModelPickerInline (desktop) — un carrusel horizontal compacto con
//     las poses + un botón "Ver más" que abre el menú full-screen.
//   • PosePickerSheet (mobile / "ver más" desktop) — bottom sheet con
//     tabs por categoría (Recomendados / Cuerpo completo / Plano medio /
//     Detalle) y grilla de poses tappable. Confirmación con "Listo (N)".
//
// El catálogo de poses se pasa como prop — fetch único arriba en
// InterfazV2 (es público, cambia poco).

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  MoreHorizontal,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  POSE_CATEGORIES,
  type PoseV2,
} from './posesApi';
import { HoverScrollCarousel } from './HoverScrollCarousel';
// PRUEBA: miniaturas de muestra en la grilla de poses (ver `posesPrueba.ts`).
import {
  POSES_PRUEBA_ACTIVAS,
  POSES_PRUEBA_AJUSTE,
  imagenDePosePrueba,
} from './posesPrueba';
import { AccionCard } from './components/AccionCard';
// El mismo interruptor que "Utilizar varios modelos" — ver
// ADR-2026-09-04-las-poses-toman-el-interruptor-de-los-modelos.
import { SwitchSeleccionMultiple } from './components/SwitchSeleccionMultiple';
import { useExitTransition } from '../../hooks/useExitTransition';
import { palabraDeModeloActual, usePalabraDeModelo } from '../../lib/nombreDeModelo';
import { ZonaDeSubida } from './components/ZonaDeSubida';
import {
  AsideDeAlta,
  ColumnaDeTrabajo,
  ColumnasDeAlta,
  TipsDeAlta,
} from './components/altaEnDosColumnas';

/**
 * Imagen del explicador de "Subir pose": foto de referencia → muñeco con la
 * misma postura. Servida desde `public/`, igual que el resto de las
 * ilustraciones de la app (ej. `/images/carrusel.png`). Es vertical (810×919),
 * por eso el contenedor la deja crecer a lo alto en vez de fijar un aspect.
 */
const POSE_EXPLAINER_IMG = '/images/pose-explainer.png';

// ─── Inline (desktop) ─────────────────────────────────────────────────

/** Cuántas cards entran en el carrusel antes de la de "Ver más". */
const INLINE_VISIBLE = 12;

interface PosePickerInlineProps {
  poses: PoseV2[];
  /** Poses propias del cliente. No están en `poses` (catálogo global) y sólo se
   *  muestran acá si están elegidas — el carrusel no es su vitrina, pero una
   *  pose elegida tiene que verse en la pantalla principal. */
  customPoses?: PoseV2[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onOpenSheet: () => void;
  /**
   * Abre el sheet de poses DIRECTO en "Subí tu propia pose".
   *
   * Sólo la usa la variante `grilla`, que dibuja una tarjeta de subir al final del
   * catálogo (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). Sin ella esa tarjeta no
   * se dibuja: el carrusel ya sale al sheet por "Ver más", y ahí adentro la de subir es
   * la primera de la grilla.
   */
  onSubirPose?: () => void;
  /** Si true, oculta el label "Pose" interno (lo provee el caller). */
  hideLabel?: boolean;
  /** Si true, fuerza selección exclusiva (radio-button). Lo usa el
   *  modo varios-modelos: con N personas en la pose no tiene sentido
   *  generar variaciones por pose. */
  singleSelect?: boolean;
  /** Deja quedarse en CERO poses (ver `toggle`). */
  permitirVacio?: boolean;
  /** Toggle "Seleccionar múltiples" (vive en el PosePickerSheet). Con
   *  false el tap reemplaza la selección en vez de agregar. Default
   *  true para no cambiar a los consumidores que no lo pasan. */
  multiPoseEnabled?: boolean;
  /**
   * Reemplaza el carrusel horizontal por una GRILLA que scrollea hacia abajo, con el
   * catálogo ENTERO (el carrusel corta en 12).
   *
   * El alto de la tarjeta sale de `--alto-fila`, una variable CSS que pone el contenedor
   * de arriba para que entre una fila y MEDIA en el alto disponible; el ancho lo reparte
   * la grilla, que llena el contenedor sin dejar sobrante
   * (ADR-2026-08-20-grilla-fila-y-media).
   */
  grilla?: boolean;
}

export const PosePickerInline: React.FC<PosePickerInlineProps> = ({
  poses,
  customPoses = [],
  selectedIds,
  onChange,
  onOpenSheet,
  onSubirPose,
  hideLabel = false,
  singleSelect = false,
  permitirVacio = false,
  multiPoseEnabled = true,
  grilla = false,
}) => {
  // El carrusel corta en 12: una pose elegida desde "Ver más" —o cualquiera de
  // las propias, que ni siquiera están en el catálogo— quedaba invisible acá, y
  // la pantalla principal no mostraba rastro de lo que estaba seleccionado. Las
  // adelantadas van PRIMERO y el corte se aplica después, así nunca las tapa.
  //
  // `adelantadas` es un pin, NO `selectedIds` en vivo: si el orden se
  // recalculara con cada tap, tocar una card la haría saltar al principio y el
  // carrusel se movería bajo el cursor. Mismo criterio que el `pinnedIdRef` del
  // ModelPicker. Se repinea sólo cuando hay una elegida que NO está visible,
  // que es justo el caso de volver del "Ver más".
  const [adelantadas, setAdelantadas] = useState<string[]>(selectedIds);
  // En grilla no hay corte: el contenedor scrollea, así que mostrar sólo 12 dejaría el
  // resto del catálogo afuera sin ninguna señal de que existe.
  const tope = grilla ? Number.POSITIVE_INFINITY : INLINE_VISIBLE;
  const visibles = React.useMemo(() => {
    const alFrente = adelantadas
      .map(
        (id) =>
          poses.find((p) => p.id === id) ??
          customPoses.find((p) => p.id === id),
      )
      .filter((p): p is PoseV2 => !!p);
    const yaEsta = new Set(alFrente.map((p) => p.id));
    const resto = poses.filter((p) => !yaEsta.has(p.id));
    return [...alFrente, ...resto].slice(
      0,
      Math.max(tope, alFrente.length),
    );
  }, [poses, customPoses, adelantadas, tope]);
  useEffect(() => {
    const hayInvisible = selectedIds.some(
      (id) => !visibles.some((v) => v.id === id),
    );
    if (hayInvisible) setAdelantadas(selectedIds);
  }, [selectedIds, visibles]);
  const toggle = (id: string) => {
    if (singleSelect || !multiPoseEnabled) {
      // Selección exclusiva: tap reemplaza. Si la selección ya es
      // exactamente esta pose, no hay nada que hacer (mínimo 1 — no se
      // deselecciona). Si venía >1 (ej. el user apagó el toggle desde
      // otro picker en un estado raro), el tap colapsa a [id].
      if (selectedIds.length === 1 && selectedIds[0] === id) return;
      onChange([id]);
      return;
    }
    if (selectedIds.includes(id)) {
      // La última pose se puede sacar sólo donde la pantalla lo banca (`permitirVacio`):
      // ahí el título lleva la cuenta y el CTA se apaga en cero, así que "ninguna" es un
      // estado visible y sin salida en falso. En el resto de los pickers sigue el mínimo
      // de 1 — son de paso, y salir de ahí sin pose dejaría al modo sin qué generar.
      if (selectedIds.length === 1 && !permitirVacio) return;
      onChange(selectedIds.filter((x) => x !== id));
      return;
    }
    onChange([...selectedIds, id]);
  };
  return (
    <div className="flex flex-col gap-3">
      {/* En grilla el catálogo entero ya está a la vista y scrollea, así que
          "Ver más" no lleva a ningún lado: la fila entera sobra (con `hideLabel`
          su único contenido era ese botón). */}
      {!grilla && (
        <div
          className={`flex items-center ${hideLabel ? 'justify-end' : 'justify-between'}`}
        >
          {!hideLabel && (
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              Pose
            </p>
          )}
          <button
            type="button"
            onClick={onOpenSheet}
            className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
          >
            Ver más
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {/* Carrusel — mismo aspect que las cards de modelo. Tap toggle.
          La `key` dispara `animate-mode-swap` cuando el catálogo
          filtrado cambia (ej. al cambiar la cantidad de modelos en
          multi mode → poses de N personas distintas). */}
      {grilla ? (
        // Grilla de columnas IGUALES que llenan el ancho exacto del contenedor
        // (ADR-2026-08-20-grilla-fila-y-media): `auto-fill` mete todas las columnas de
        // al menos `9/16 del alto de fila` que entren, y el `1fr` reparte lo que sobra
        // ENTRE ellas. Con `flex-wrap` y ancho fijo por aspect, ese resto quedaba como
        // una franja blanca al final de cada fila.
        //
        // El alto manda (`--alto-fila`) y el ancho lo pone la columna, así que las
        // tarjetas terminan un pelo más anchas que 9:16 — el `object-cover` de la foto
        // absorbe la diferencia. Al revés (ancho fijo, alto por aspect) las filas no
        // cerrarían en la fracción exacta que deja asomar la de abajo.
        //
        // `content-start`: las filas se apilan desde arriba en vez de repartirse el alto
        // sobrante, que con pocas poses las dejaba flotando en el medio.
        <div
          className="grid content-start gap-3 animate-mode-swap"
          style={{
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(calc(var(--alto-fila, 240px) * 9 / 16), 100%), 1fr))',
          }}
        >
          {visibles.map((p, i) => {
            const isSel = selectedIds.includes(p.id);
            // PRUEBA: la miniatura sale de las imágenes de muestra, alternadas por
            // posición. La pose (id, nombre, prompt) no cambia — sólo la foto.
            const thumb = POSES_PRUEBA_ACTIVAS
              ? imagenDePosePrueba(i)
              : p.imageUrl;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`h-[var(--alto-fila,240px)] w-full rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
                  isSel ? 'border-brand-primary' : 'border-neutral-200'
                } transition-all relative active:scale-[0.97]`}
              >
                {thumb && (
                  <img
                    src={thumb}
                    alt={p.nombre}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full ${
                      // PRUEBA: `object-cover` es lo de siempre; el sandbox puede pedir
                      // `contain` porque sus imágenes son cuadradas.
                      POSES_PRUEBA_ACTIVAS && POSES_PRUEBA_AJUSTE === 'contain'
                        ? 'object-contain'
                        : 'object-cover'
                    }`}
                  />
                )}
                {/* El nombre DENTRO de la foto, abajo, sobre un degradé negro corto —
                    ADR-2026-09-04-las-poses-muestran-su-nombre-sobre-la-foto. Es la misma
                    anatomía que las tarjetas de PRODUCTO de este modo
                    (`nombreSobreLaFoto` en `PickProductPanelModelo`): el degradé arranca
                    transparente y no llega ni a un sexto de la tarjeta, así que separa el
                    texto del fondo sin tapar la pose. */}
                <span className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                  <span className="block text-left text-[12px] font-bold text-white truncate">
                    {p.nombre}
                  </span>
                </span>
                {isSel && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
          {/* Cierre del catálogo: "Subir pose". Va ÚLTIMA y no primera —al revés que
              en el sheet, donde compite con un catálogo paginado por categorías— porque
              acá la grilla es el catálogo entero y el orden cuenta una historia: mirá
              todas las poses que hay y, si ninguna sirve, subí la tuya
              (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). Mide igual que una
              tarjeta de pose: es una card más de la grilla, no un botón aparte. */}
          {onSubirPose && (
            <AccionCard
              className="h-[var(--alto-fila,240px)] w-full"
              label="Subir pose"
              sublabel="Usá una foto tuya"
              icon={<Plus className="w-5 h-5" strokeWidth={2.5} />}
              onClick={onSubirPose}
            />
          )}
        </div>
      ) : (
      <HoverScrollCarousel
        key={`${poses.length}-${poses[0]?.id ?? ''}`}
        className="flex gap-3 overflow-x-auto -mx-5 px-5 scrollbar-hide animate-mode-swap"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        arrowInset={4}
      >
        {visibles.map((p) => {
          const isSel = selectedIds.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              className={`shrink-0 w-[140px] aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
                isSel ? 'border-brand-primary' : 'border-neutral-200'
              } transition-all relative active:scale-[0.97]`}
            >
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt={p.nombre}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              )}
              {/* El nombre DENTRO de la foto, abajo, sobre un degradé negro corto —
                  ADR-2026-09-04-las-poses-muestran-su-nombre-sobre-la-foto. Es la misma
                  anatomía que las tarjetas de PRODUCTO de este modo
                  (`nombreSobreLaFoto` en `PickProductPanelModelo`): el degradé arranca
                  transparente y no llega ni a un sexto de la tarjeta, así que separa el
                  texto del fondo sin tapar la pose. */}
              <span className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                <span className="block text-left text-[12px] font-bold text-white truncate">
                  {p.nombre}
                </span>
              </span>
              {isSel && (
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
        {/* Última card: el carrusel corta en 12 poses, así que acá la salida a
            la grilla completa no es sólo un atajo — es la única forma de ver
            el resto sin volver al link del header. */}
        <AccionCard
          className="shrink-0 w-[140px] aspect-[9/16]"
          sublabel={`${poses.length} poses`}
          onClick={onOpenSheet}
        />
      </HoverScrollCarousel>
      )}
    </div>
  );
};

// ─── Sheet (mobile + "ver más" desktop) ───────────────────────────────

/**
 * La pantalla "Subí tu propia pose": qué hace y cómo tiene que ser la foto, con el CTA
 * que abre el file picker. La usan el sheet de acá abajo y la biblioteca de adjuntos
 * del Agente (ADR-2026-09-12-la-biblioteca-agrega-en-todas-las-pestanas), así hay UNA
 * sola versión de esta pantalla. El input de archivo lo pone el host: acá sólo se pide
 * abrirlo. El "Volver" es de desktop (en mobile lo pone la barra del sheet).
 */
export const ExplicadorSubirPose: React.FC<{
  uploading: boolean;
  onVolver: () => void;
  onElegirFoto: () => void;
  /** El host ya pone el "Volver" (la flecha de su cabecera): acá sobra
   *  — ADR-2026-09-22-las-altas-de-todas-las-fuentes-en-el-selector. */
  sinVolver?: boolean;
  /**
   * La pantalla ocupa una ventana ANCHA (el selector de fuentes de la caja del Agente,
   * 980): la zona de subida a la izquierda, el ejemplo del proceso y los tips a la
   * derecha — ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas. No lleva
   * barra de CTA: elegir la foto YA la manda a procesar, así que no hay nada que
   * confirmar después. El título lo pone el host.
   */
  ancho?: boolean;
  /**
   * Sólo en modo ancho: la foto elegida desde la zona de subida. El host sigue siendo el
   * que la sube (tarda), igual que con el input que dispara `onElegirFoto`.
   */
  onFoto?: (archivo: File) => void;
}> = ({ uploading, onVolver, onElegirFoto, sinVolver = false, ancho = false, onFoto }) =>
  ancho ? (
    <ColumnasDeAlta>
      <ColumnaDeTrabajo>
        <ZonaDeSubida
          alto="alta"
          titulo={uploading ? 'Creando la pose…' : 'Subí la foto con la pose'}
          ayuda="Arrastrala acá o hacé click para elegirla. Se copia sólo la postura: ni la persona ni el fondo."
          deshabilitada={uploading}
          onArchivos={(archivos) => {
            const elegida = archivos[0];
            if (elegida) onFoto?.(elegida);
          }}
        />
      </ColumnaDeTrabajo>
      <AsideDeAlta titulo="De la foto a la pose">
        {POSE_EXPLAINER_IMG ? (
          <img
            src={POSE_EXPLAINER_IMG}
            alt="De una foto de referencia a la pose lista para usar"
            loading="lazy"
            className="mx-auto max-h-[280px] w-auto max-w-full object-contain"
          />
        ) : (
          <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 rounded-2xl border border-neutral-200 bg-white text-[#3C3C43]/40">
            <ImageIcon className="h-7 w-7" />
            <span className="text-[12.5px]">Ejemplo del proceso</span>
          </div>
        )}
        <TipsDeAlta
          items={[
            'Una foto nítida y de frente al cuerpo funciona mejor.',
            'El fondo y la ropa no importan: sólo se copia la postura, no la persona.',
            'Tarda un momento en procesarse. Cuando termina, aparece primera en "Mis poses".',
          ]}
        />
      </AsideDeAlta>
    </ColumnasDeAlta>
  ) : (
    <div className="flex flex-col gap-4 min-h-full">
      {/* En mobile el "Volver" y el título viven en la barra de arriba del
          sheet (reemplazan a "Cancelar"), así que acá van solo en desktop. */}
      {!sinVolver && (
        <button
          type="button"
          onClick={onVolver}
          className="hidden md:inline-flex items-center gap-1 text-brand-primary text-[13px] hover:opacity-75 transition-opacity self-start"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Volver
        </button>
      )}

      <div>
        <p className="hidden md:block text-[16px] font-semibold text-brand-black/90">
          Subí tu propia pose
        </p>
        <p className="text-[13.5px] text-[#3C3C43]/65 mt-1 leading-relaxed">
          Subí una foto con la postura que te gusta y Atlas la guarda como una
          pose tuya. Después la elegís igual que cualquier otra del catálogo,
          para generar con tu {palabraDeModeloActual().singular} y tu producto.
        </p>
      </div>

      {/* Ejemplo del proceso (foto → muñeco). La imagen es vertical y ya trae
          su propio fondo blanco: sin borde ni card, y con `max-h` para que en
          mobile no se coma toda la altura del sheet. */}
      <div className="w-full flex items-center justify-center">
        {POSE_EXPLAINER_IMG ? (
          <img
            src={POSE_EXPLAINER_IMG}
            alt="De una foto de referencia a la pose lista para usar"
            loading="lazy"
            className="w-auto max-w-full max-h-[300px] md:max-h-[340px] object-contain"
          />
        ) : (
          <div className="w-full aspect-[4/5] max-h-[300px] rounded-2xl bg-neutral-50 border border-neutral-200 flex flex-col items-center justify-center gap-2 text-[#3C3C43]/40">
            <ImageIcon className="w-7 h-7" />
            <span className="text-[12.5px]">Ejemplo del proceso</span>
          </div>
        )}
      </div>

      <ul className="text-[13px] text-[#3C3C43]/65 list-disc pl-4 space-y-1.5 leading-snug">
        <li>Una foto nítida y de frente al cuerpo funciona mejor.</li>
        <li>
          El fondo y la ropa no importan: sólo se copia la postura, no la
          persona.
        </li>
      </ul>

      <button
        type="button"
        onClick={onElegirFoto}
        disabled={uploading}
        className="mt-auto w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        {uploading ? (
          <>
            <span className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            Creando la pose…
          </>
        ) : (
          <>
            <Upload className="w-5 h-5" />
            Subir imagen con la pose
          </>
        )}
      </button>
    </div>
);

interface PosePickerSheetProps {
  open: boolean;
  poses: PoseV2[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
  /**
   * En qué pantalla abre el sheet: la grilla de poses (default) o el explicador de
   * "Subí tu propia pose". Lo usa la tarjeta de subir que cierra la grilla inline
   * (ADR-2026-08-20-subir-pose-al-final-de-la-grilla): el usuario ya dijo qué quiere
   * hacer, y pasar por la grilla del sheet para volver a decirlo es un paso de más.
   */
  vistaInicial?: 'list' | 'subir';
  /** Si true, fuerza selección exclusiva — mismo motivo que en el
   *  Inline: el modo varios-modelos no soporta multi-pose. */
  singleSelect?: boolean;
  /** Estado del toggle "Seleccionar múltiples" (controlado por el
   *  caller). Solo con el toggle ON se puede elegir más de una pose. */
  multiPoseEnabled?: boolean;
  /** Commit del toggle al confirmar con "Listo/Aceptar". Si NO se pasa,
   *  el sheet no muestra el switch y conserva el multi-select clásico
   *  (consumidores viejos, ej. el flujo del agente). */
  onMultiPoseEnabledChange?: (enabled: boolean) => void;
  /** Poses personalizadas del cliente (tab "Mis poses"). */
  customPoses?: PoseV2[];
  /** Sube una pose nueva (dispara el flujo n8n). */
  onUploadPose?: (file: File) => void;
  /** Borra una pose propia. Si devuelve una promesa, el modal de confirmación
   *  espera a que resuelva mostrando "Eliminando…" — el borrado real (storage +
   *  fila) tarda un par de segundos. */
  onDeletePose?: (pose: PoseV2) => void | Promise<void>;
  /** true mientras se procesa una subida (spinner en el card "+"). */
  uploading?: boolean;
}

export const PosePickerSheet: React.FC<PosePickerSheetProps> = ({
  open,
  poses,
  selectedIds,
  onChange,
  onClose,
  vistaInicial = 'list',
  singleSelect = false,
  multiPoseEnabled = true,
  onMultiPoseEnabledChange,
  customPoses = [],
  onUploadPose,
  onDeletePose,
  uploading = false,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const [tab, setTab] = useState<string>('recomendado');
  const fileRef = useRef<HTMLInputElement>(null);
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al seleccionar Listo /
  // Cancelar / overlay) — así el deslizamiento inverso se ve antes de
  // que el sheet desaparezca del DOM.
  const { mounted, animated } = useExitTransition(open, 300);
  // Working copy — solo commiteamos al cerrar con "Listo". Si el user
  // toca Cancelar / overlay, descartamos los cambios. El toggle
  // "Seleccionar múltiples" es parte del draft por el mismo motivo.
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);
  const [draftMulti, setDraftMulti] = useState<boolean>(multiPoseEnabled);
  // Vista del sheet: la grilla de poses o la pantalla que explica cómo subir
  // una propia. El "Subir pose" ya no abre el file picker de una: sin contexto,
  // el user sube cualquier foto y el muñeco sale mal.
  const [view, setView] = useState<'list' | 'subir'>('list');
  // Mobile: id de la pose propia cuyo menú de opciones está abierto. En
  // desktop el borrar sigue apareciendo al hacer hover sobre la card.
  const [menuPoseId, setMenuPoseId] = useState<string | null>(null);
  // Pose pendiente de confirmar borrado (null = modal cerrado).
  const [poseAEliminar, setPoseAEliminar] = useState<PoseV2 | null>(null);
  const [eliminando, setEliminando] = useState(false);
  useEffect(() => {
    if (open) {
      setDraftIds(selectedIds);
      setDraftMulti(multiPoseEnabled);
      // Cada apertura arranca donde diga el que abre —la grilla salvo que se pida el
      // explicador—: el sheet queda montado entre aperturas, así que sin esto reabriría
      // en la vista en la que lo dejaron.
      setView(vistaInicial);
      setMenuPoseId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!mounted) return null;

  // El switch existe solo si el caller lo controla (design-modelo) y no
  // estamos forzados a pose única por multi-modelo.
  const multiToggleAvailable = !!onMultiPoseEnabledChange && !singleSelect;
  // ¿Se permite acumular poses en el draft? Sin control externo se
  // conserva el multi clásico (backwards-compat con el flujo del agente).
  const allowMulti =
    !singleSelect && (multiToggleAvailable ? draftMulti : true);

  const toggle = (id: string) => {
    setDraftIds((prev) => {
      if (!allowMulti) {
        // Selección exclusiva (multi-modelo o toggle OFF): el tap
        // reemplaza. Mínimo 1 — tap sobre la única elegida la mantiene.
        if (prev.length === 1 && prev[0] === id) return prev;
        return [id];
      }
      if (prev.includes(id)) {
        // No permitimos deseleccionar la última pose: siempre queda ≥1.
        if (prev.length === 1) return prev;
        return prev.filter((x) => x !== id);
      }
      // Se pueden mezclar "Mis poses" con las del catálogo en la misma tanda:
      // el backend las recompone TODAS sobre la misma imagen base, así que
      // salen con el mismo modelo y el mismo fondo (ver ADR-607, que ya lo
      // había habilitado para el Catálogo IA).
      return [...prev, id];
    });
  };
  // Apagar el toggle con varias poses en el draft colapsa a la PRIMERA
  // (mismo criterio que la interfaz anterior, PruebaNuevaInterfaz).
  const handleDraftMultiChange = (enabled: boolean) => {
    setDraftMulti(enabled);
    if (!enabled) {
      setDraftIds((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  };
  // Borrado de una pose propia: abre la confirmación in-app (antes era un
  // `window.confirm`, que rompía con la UI del resto de la app). Lo disparan
  // el hover de desktop y el menú "…" de mobile.
  const pedirBorrado = (p: PoseV2) => {
    setMenuPoseId(null);
    setPoseAEliminar(p);
  };
  // El modal se queda abierto —con el botón en "Eliminando…"— hasta que el
  // borrado real termina. Cerrarlo al toque dejaba la card todavía en la
  // grilla un par de segundos, como si el borrado no hubiera pasado.
  const eliminarConfirmado = async () => {
    if (!poseAEliminar || eliminando) return;
    setEliminando(true);
    try {
      await onDeletePose?.(poseAEliminar);
    } finally {
      setEliminando(false);
      setPoseAEliminar(null);
    }
  };
  const handleConfirm = () => {
    // El toggle se commitea ANTES que las poses: si el caller validara
    // la selección contra el estado del toggle, ya vería el nuevo.
    if (multiToggleAvailable) onMultiPoseEnabledChange!(draftMulti);
    onChange(draftIds);
    onClose();
  };

  // Filtra poses por categoría. Para 'recomendado' no tenemos un signal
  // de productModelViews en este nivel — mostramos todas como recomendadas
  // para no quedarnos sin contenido. El caller puede pasar un orden custom
  // a futuro si quiere priorizar algunas.
  const isMine = tab === 'mis_poses';
  const filtered = isMine
    ? customPoses
    : tab === 'recomendado'
      ? poses
      : poses.filter((p) => (p.categoria || '') === tab);

  // ── Pantalla "Subir pose": qué hace y cómo tiene que ser la foto ──────
  // Va antes del file picker a propósito: el resultado depende bastante de la
  // foto que se elija, y sin esta pantalla el user no tiene forma de saberlo.
  // `min-h-full` + `mt-auto` en el CTA: el botón queda pegado al fondo del
  // panel aunque el contenido no llegue a llenarlo, en vez de flotar a media
  // altura con un vacío abajo.
  // ── Pantalla "Subir pose": la misma que usa la biblioteca del Agente ──
  const explicadorSubir = (
    <ExplicadorSubirPose
      uploading={uploading}
      onVolver={() => setView('list')}
      onElegirFoto={() => fileRef.current?.click()}
    />
  );

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile la altura del sheet (en className) ya excluye el
          // safe area top — bottom-anclado sin zona blanca arriba.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* ── Mobile: drag handle + iOS header (Cancelar / título / Listo) ── */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* En el explicador esta barra pasa a ser la navegación de esa
            pantalla: "Volver" en lugar de "Cancelar" y sin "Listo" (no hay nada
            que confirmar ahí — es el mismo criterio que el footer de desktop).
            El spacer mantiene el título centrado. */}
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          {view === 'subir' ? (
            <button
              type="button"
              onClick={() => setView('list')}
              className="inline-flex items-center gap-0.5 text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
            >
              Cancelar
            </button>
          )}
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            {view === 'subir' ? 'Subí tu propia pose' : 'Elegí poses'}
          </h3>
          {view === 'subir' ? (
            <span className="w-20" aria-hidden />
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={draftIds.length === 0}
              className="text-[15px] text-brand-primary font-semibold disabled:opacity-40 active:opacity-50 transition-opacity w-20 text-right"
            >
              {draftIds.length > 0 ? `Listo (${draftIds.length})` : 'Listo'}
            </button>
          )}
        </div>

        {/* ── Desktop: header título + X close, divider abajo (PruebaNueva). ── */}
        <div className="hidden md:flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            Pose del {palabra.singular}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hidden md:block mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Contenido — tabs + grilla. Padding y aspect cambian por
            breakpoint. Desktop usa px-5 + aspect 4/5 (PruebaNueva style),
            mobile sigue con px-4 + aspect 9/16. */}
        <div className="flex-1 overflow-y-auto px-4 md:px-5 pt-2 md:pt-4 pb-4">
          {view === 'subir' ? (
            explicadorSubir
          ) : (
          <>
          {/* El interruptor que pasa la grilla de selección ÚNICA a multi-select —
              ADR-2026-09-04-las-poses-toman-el-interruptor-de-los-modelos.

              Es el MISMO control que "Utilizar varios modelos"
              (`SwitchSeleccionMultiple`) y no la fila con el switch de iOS que había
              acá: dos grillas del mismo modo ofrecían la misma decisión con dos
              controles distintos, y el de poses —una fila suelta arriba de las
              pastillas de categoría— se leía como un ajuste más de la lista y no como
              algo que cambia lo que hace cada tap. La cuenta de elegidas, que antes iba
              en un renglón gris aparte, ahora es la bajada del propio interruptor.

              Sólo cuando el caller lo controla (design-modelo) y no estamos forzados a
              pose única por multi-modelo. Apagado —el default— cada tap REEMPLAZA. */}
          {multiToggleAvailable && (
            <SwitchSeleccionMultiple
              titulo="Utilizar varias poses"
              activo={draftMulti}
              onChange={handleDraftMultiChange}
              // La bajada dice la CONSECUENCIA antes de que se note en la pastilla de
              // créditos: cada pose elegida es una imagen más de la tanda.
              bajada={
                !draftMulti
                  ? 'Se genera una imagen por cada pose que elijas.'
                  : `${draftIds.length} ${
                      draftIds.length === 1 ? 'pose elegida' : 'poses elegidas'
                    } — una imagen por cada una.`
              }
            />
          )}
          {/* Tabs */}
          <div
            className="flex flex-wrap gap-2 mb-3 md:mb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {[
              ...POSE_CATEGORIES,
              { id: 'mis_poses', label: 'Mis poses' },
            ].map((c) => {
              const isActive = tab === c.id;
              const isRecomendado = c.id === 'recomendado';
              const isMineTab = c.id === 'mis_poses';
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setTab(c.id)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 md:px-4 h-9 rounded-full border text-[13px] md:text-[15px] font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-brand-black/90 border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  {isRecomendado && <Sparkles className="w-3.5 h-3.5" />}
                  {isMineTab && <Plus className="w-3.5 h-3.5" />}
                  {c.label}
                </button>
              );
            })}
          </div>
          {/* El mensaje de categoría vacía ya no reemplaza a la grilla: la
              acompaña, para que la card de subir pose siga estando también en
              una categoría sin resultados. */}
          {!isMine && filtered.length === 0 && (
            <p className="text-[13.5px] text-[#3C3C43]/55 mt-6 mb-4 text-center">
              No hay poses en esta categoría.
            </p>
          )}
          {
            // `key` por categoría + tamaño del catálogo: anima cuando
            // el user cambia de tab o cuando el filtro externo (la
            // cantidad esperada de personas en la pose) muta el set.
            <div
              key={`${tab}-${filtered.length}-${filtered[0]?.id ?? ''}`}
              className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3 animate-mode-swap"
            >
              {/* Card "Subir pose" — primera de TODAS las categorías, no sólo
                  de "Mis poses": subir una pose propia es una acción que el
                  user quiere tener a mano mientras mira el catálogo, sin tener
                  que descubrir primero que existe la tab.
                  Estira (`md:h-full`) en vez de fijar su alto por aspect: en desktop la
                  card de pose es 4:5 y con `aspect-[9/16]` ésta quedaba más baja que sus
                  vecinas. El `min-h` es para el caso en que sea la ÚNICA card de la
                  grilla (categoría vacía), donde no hay vecina que defina el alto de la
                  fila. */}
              <AccionCard
                className="w-full aspect-[9/16] md:aspect-auto md:h-full md:min-h-[240px]"
                label="Subir pose"
                icon={<Plus className="w-5 h-5" strokeWidth={2.5} />}
                loading={uploading}
                loadingLabel="Creando…"
                onClick={() => setView('subir')}
              />

              {filtered.map((p) => {
                const isSel = draftIds.includes(p.id);
                const card = (
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    className={`relative w-full rounded-xl md:rounded-[13px] overflow-hidden bg-white border-2 transition-all active:scale-[0.97] ${
                      isSel
                        ? 'border-brand-primary md:border-transparent md:ring-2 md:ring-brand-primary'
                        : 'border-neutral-200 md:border-transparent md:ring-1 md:ring-black/[0.06]'
                    }`}
                  >
                    {p.imageUrl && (
                      <img
                        src={p.imageUrl}
                        alt={p.nombre}
                        loading="lazy"
                        className={`block w-full aspect-[9/16] md:aspect-[4/5] bg-neutral-100 ${
                          p.isCustom
                            ? 'object-cover'
                            : 'object-cover md:object-[center_15%]'
                        }`}
                      />
                    )}
                    {isSel && (
                      <span className="absolute top-1.5 right-1.5 md:top-2.5 md:right-2.5 w-6 h-6 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      </span>
                    )}
                    {/* Multi ON: círculo vacío en las no seleccionadas —
                        señal visual de que se pueden ir sumando (mismo
                        affordance que la interfaz anterior). */}
                    {allowMulti && multiToggleAvailable && !isSel && (
                      <span className="absolute top-1.5 right-1.5 md:top-2.5 md:right-2.5 w-6 h-6 rounded-full border-2 border-black/[0.15] bg-white/80" />
                    )}
                    {/* El nombre DENTRO de la foto, abajo, sobre un degradé negro corto —
                        ADR-2026-09-04-las-poses-muestran-su-nombre-sobre-la-foto.

                        Antes era un renglón DEBAJO de la imagen y sólo en desktop: en el
                        teléfono la card no decía qué pose era —había que deducirla del
                        dibujo— y las dos superficies mostraban cosas distintas. Adentro,
                        el nombre viaja con la foto y la card mide lo mismo en las dos.

                        Las custom también lo muestran: ya no comparten el literal "Mi
                        pose", vienen numeradas ("Pose propia 1", 2…) desde
                        `posesCustomApi`. */}
                    <span className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                      <span className="block text-left text-[12px] md:text-[13px] font-bold text-white truncate">
                        {p.nombre}
                      </span>
                    </span>
                  </button>
                );
                if (!isMine) return <React.Fragment key={p.id}>{card}</React.Fragment>;
                // En "Mis poses": wrapper relativo con las acciones (no se
                // pueden anidar <button>, por eso son hermanas del card).
                const menuAbierto = menuPoseId === p.id;
                return (
                  <div key={p.id} className="relative group">
                    {card}
                    {/* Desktop: 🗑️ directo, al hacer hover sobre la card. */}
                    <button
                      type="button"
                      onClick={() => pedirBorrado(p)}
                      aria-label="Borrar pose"
                      className="hidden md:inline-flex absolute md:top-2.5 md:left-2.5 w-6 h-6 rounded-full items-center justify-center shadow-md ring-2 ring-white bg-white text-red-500 hover:bg-red-50 transition md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {/* Mobile: sin hover, el 🗑️ siempre visible ensuciaba la
                        grilla entera. Ahora aparecen los "…" sólo en la pose
                        seleccionada, y el borrar vive en su menú. */}
                    {isSel && (
                      <button
                        type="button"
                        onClick={() =>
                          setMenuPoseId(menuAbierto ? null : p.id)
                        }
                        aria-label="Opciones de la pose"
                        aria-expanded={menuAbierto}
                        // Sin `ring-2 ring-white`: en el check el anillo blanco
                        // separa el disco azul de la foto, pero acá el círculo
                        // YA es blanco — anillo y círculo se funden y el botón
                        // se ve 4px más grande que su par. El shadow alcanza
                        // para despegarlo del fondo.
                        className="md:hidden absolute top-1.5 left-1.5 w-6 h-6 rounded-full inline-flex items-center justify-center shadow-md bg-white text-brand-black/70 active:bg-neutral-100 transition-colors"
                      >
                        <MoreHorizontal className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    )}
                    {menuAbierto && (
                      <>
                        {/* Capta el tap de afuera para cerrar. */}
                        <div
                          className="md:hidden fixed inset-0 z-10"
                          onClick={() => setMenuPoseId(null)}
                        />
                        <div className="md:hidden absolute top-[34px] left-1.5 z-20 min-w-[130px] rounded-xl bg-white border border-neutral-200 shadow-[0_8px_24px_rgba(0,0,0,0.14)] overflow-hidden">
                          <button
                            type="button"
                            onClick={() => pedirBorrado(p)}
                            className="w-full inline-flex items-center gap-2 px-3 py-2.5 text-[13.5px] font-medium text-red-500 active:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Eliminar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          }
          </>
          )}

          {/* Input file oculto — vive FUERA del switch de vista porque lo
              disparan las dos: el CTA del explicador y (histórico) el card. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                onUploadPose?.(f);
                // La pose recién subida aterriza en "Mis poses". Volvemos a la
                // grilla en esa tab: ahí se ve el spinner del card y después
                // aparece la pose. Quedarse en el explicador dejaría al user
                // mirando una pantalla estática mientras se procesa.
                setTab('mis_poses');
                setView('list');
              }
              e.currentTarget.value = '';
            }}
          />
        </div>

        {/* ── Desktop footer: Cancelar / Aceptar ──
            No va en el explicador de "Subir pose": ahí no hay nada que
            aceptar, y un "Aceptar (1)" al pie confundía con el CTA real de esa
            pantalla ("Elegir foto"). La salida de ahí es "Volver". */}
        {view === 'list' && (
        <div className="hidden md:flex px-5 py-3 justify-end gap-3 shrink-0 border-t border-black/[0.06]">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={draftIds.length === 0}
            className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
              draftIds.length > 0
                ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
                : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
            }`}
          >
            {draftIds.length > 0 ? `Aceptar (${draftIds.length})` : 'Aceptar'}
          </button>
        </div>
        )}
      </div>

      {/* Confirmación de borrado — misma estructura que `FeatureLockedModal`
          (círculo con ícono + título + mensaje + Cancelar/CTA), en versión
          destructiva. Va POR ENCIMA del sheet (z-[9999] > z-[211]): el sheet
          sigue visible atrás, así no se pierde el contexto de qué se borra. */}
      {poseAEliminar && (
        <div className="fixed inset-0 z-[9999] bg-brand-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.18)] w-full max-w-md mx-4 p-5 animate-modal-appear">
            <div className="flex justify-center mb-4">
              <span className="w-12 h-12 rounded-full bg-red-50 inline-flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-500" />
              </span>
            </div>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2 text-center">
              Eliminar pose
            </h3>
            <p className="text-[15px] text-[#3C3C43]/65 mb-6 text-center leading-relaxed">
              ¿Seguro que querés eliminar{' '}
              <span className="font-medium text-brand-black/85">
                {poseAEliminar.nombre}
              </span>
              ? No se puede deshacer.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center">
              <button
                type="button"
                onClick={() => setPoseAEliminar(null)}
                disabled={eliminando}
                className="inline-flex items-center justify-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-white text-brand-black/90 text-[15px] font-medium rounded-xl shadow-sm hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={eliminarConfirmado}
                disabled={eliminando}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 w-full sm:w-auto bg-red-500 hover:bg-red-600 disabled:bg-red-500/70 disabled:cursor-not-allowed text-white text-[15px] font-semibold rounded-xl shadow-sm transition-colors"
              >
                {eliminando && (
                  <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                )}
                {eliminando ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
};

// ─── Helper para mostrar el resumen de selección ──────────────────────

export const poseSelectionLabel = (ids: string[], poses: PoseV2[]): string => {
  if (ids.length === 0) return 'Sugerido';
  if (ids.length === 1) {
    const p = poses.find((x) => x.id === ids[0]);
    return p?.nombre || 'Pose';
  }
  return `${ids.length} poses`;
};

// Silenciador de imports no usados (mantengo símbolos por si se extiende)
void ChevronLeft;
void X;
